import { _notNil, _isNil } from '@/littledash';
import { ScoringScaleEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/ScoringScaleEditorV16';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import type { RefObject } from 'react';
import {
  validateObservationTypeTitle,
  validateObservationTypeLabel,
  getBasicObservationTypeErrorMessage,
  getBasicLabelErrorMessage,
  type ObservationTypeRow,
  type ValidationData,
  type ValidationResult,
} from '@/utils/observationTypeValidation';

type ValidatorCallback = (valid: boolean) => void;

interface BuildColumnsV16Params {
  validationData: ValidationData | null;
  hotTableRef: RefObject<HotTableRef | null>;
}

export const buildColumnsV16 = ({
  validationData,
  hotTableRef,
}: BuildColumnsV16Params): SpreadsheetV16Settings['columns'] => {
  const getSpreadsheetData = (): Array<ObservationTypeRow> => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_notNil(hotInstance) && typeof (hotInstance as { getData?: unknown }).getData === 'function') {
      return (hotInstance as { getData: () => Array<ObservationTypeRow> }).getData();
    }
    return [];
  };

  const validateTitle = (value: string, rowIndex: number): ValidationResult => {
    return validateObservationTypeTitle(value, rowIndex, validationData, [], getSpreadsheetData());
  };

  const validateLabel = (value: string, rowIndex: number): ValidationResult => {
    return validateObservationTypeLabel(value, rowIndex, validationData, [], getSpreadsheetData());
  };

  return [
    {
      data: 0,
      value: 'Observation type',
      type: 'text',
      className: 'htMiddle',
      width: 200,
      validator: function (this: { row: number }, value: unknown, callback: ValidatorCallback) {
        if (_isNil(value) || typeof value !== 'string') {
          callback(false);
          return;
        }

        const rowIndex = this.row;
        if (typeof rowIndex !== 'number') {
          callback(true);
          return;
        }

        const validation = validateTitle(value, rowIndex);
        callback(validation.isValid);
      },
      validationErrorMessage: (value: unknown) => {
        return getBasicObservationTypeErrorMessage(value as string, validationData);
      },
      linkedValidationColumns: ['0'],
    },
    {
      data: 1,
      value: 'Label',
      type: 'text',
      className: 'htMiddle',
      width: 120,
      validator: function (this: { row: number }, value: unknown, callback: ValidatorCallback) {
        if (_isNil(value)) {
          callback(true);
          return;
        }

        if (typeof value !== 'string') {
          callback(false);
          return;
        }

        const rowIndex = this.row;
        if (typeof rowIndex !== 'number') {
          callback(true);
          return;
        }

        const validation = validateLabel(value, rowIndex);
        callback(validation.isValid);
      },
      validationErrorMessage: (value: unknown) => {
        return getBasicLabelErrorMessage(value as string, validationData);
      },
      linkedValidationColumns: ['1'],
    },
    {
      data: 2,
      value: 'Scoring scale',
      type: ScoringScaleEditorV16.type,
      className: 'htMiddle',
      width: 200,
    },
    {
      data: 3,
      value: 'Grading information',
      type: 'text',
      className: 'htMiddle',
      width: 200,
    },
  ];
};

export const initBulkAddObservationTypesSettingsV16 = (
  columns: SpreadsheetV16Settings['columns']
): SpreadsheetV16Settings => ({
  columns,
  colHeaders: ['Observation type', 'Label', 'Scoring scale', 'Grading information'],
  rowHeaders: true,
  manualColumnResize: true,
  contextMenu: {
    items: {
      row_above: {},
      row_below: {},
      remove_row: {},
    },
  },
  outsideClickDeselects: false,
  height: 400,
  rowHeights: 50,
  className: 'htMiddle',
  stretchH: 'all' as const,
  minSpareRows: 1,
});
